import { api } from "../api";
import { confirmDialog } from "../components/confirm-dialog";
import { showToast } from "../utils/toast";
import { isoDate, formatDate } from "../utils/date-utils";
import type { StudentAbsence } from "../types";
import { esc } from "../utils/html";

/**
 * „Meine Abwesenheiten" im Schüler-Portal.
 *
 * Zwei Dinge an einem Ort: sich abmelden, und sehen, wie viel man bisher versäumt hat.
 * Der zweite Teil ist der eigentliche Grund für die Seite, denn an den Fehlzeiten hängt
 * die Zulassung zur staatlichen Prüfung.
 *
 * Bewusst ohne Grundangabe. Ein Freitextfeld füllt sich mit „Grippe" und „Reha", und das
 * sind Gesundheitsdaten. Ob eine Abwesenheit entschuldigt ist, entscheidet ohnehin die
 * Schule, nicht der Schüler; eine Selbstmeldung geht deshalb als „gemeldet" ein.
 *
 * Aufbau wie „Mein Stundenplan": Karten statt Tabelle. Auf einem Telefon ist eine Zeile
 * mit vier Spalten nicht zu lesen, und das Portal wird überwiegend dort benutzt. Aus
 * demselben Grund zeigt das Formular im Normalfall nur zwei Felder; Uhrzeiten kommen
 * erst dazu, wenn jemand wirklich nur einen Teil des Tages fehlt.
 */

let absences: StudentAbsence[] = [];
let summary: { absentHours: number; heldHours: number } | null = null;
let currentEl: HTMLElement | null = null;

const KIND_LABEL: Record<string, string> = {
  gemeldet: "Eingegangen",
  entschuldigt: "Entschuldigt",
  unentschuldigt: "Unentschuldigt",
  beurlaubt: "Beurlaubt",
};

/** Farbe des Kennzeichens: nur „unentschuldigt" ist eine schlechte Nachricht. */
function kindBadge(a: StudentAbsence): string {
  if (a.status !== "aktiv") return `<span class="badge badge-gray">Zurückgenommen</span>`;
  const cls = a.kind === "unentschuldigt" ? "badge-gray"
    : a.kind === "gemeldet" ? "badge-blue" : "badge-green";
  return `<span class="badge ${cls}">${esc(KIND_LABEL[a.kind] || a.kind)}</span>`;
}

function period(a: StudentAbsence): string {
  return a.endDate !== a.startDate
    ? `${formatDate(a.startDate)} bis ${formatDate(a.endDate)}`
    : formatDate(a.startDate);
}

/**
 * Zurücknehmen darf man nur die eigene Meldung, und nur solange die Schule sie noch nicht
 * bearbeitet hat. Danach ist sie deren Entscheidung; sonst ließe sich ein
 * „unentschuldigt" mit einem Klick wegräumen. Was die Schule selbst eingetragen hat,
 * gehört ohnehin nicht dem Schüler. Das Backend prüft dasselbe noch einmal.
 */
function canRetract(a: StudentAbsence): boolean {
  return a.status === "aktiv" && a.kind === "gemeldet" && a.source === "schueler";
}

function renderList(): string {
  if (absences.length === 0) {
    return `<div class="empty-state--inline">Du hast dich noch nicht abgemeldet.</div>`;
  }
  return `
    <div class="card">
      ${absences.map(a => `
        <div class="st-absence${a.status === "aktiv" ? "" : " st-absence--retracted"}">
          <div class="st-absence-main">
            <div class="st-absence-period">${esc(period(a))}</div>
            <div class="st-absence-meta">
              ${a.startTime ? `${esc(a.startTime)} bis ${esc(a.endTime!)}` : "ganztags"}${a.attestVorgelegt ? " · Attest liegt vor" : ""}${a.source === "schule" ? " · von deiner Schule eingetragen" : ""}
            </div>
            ${a.status === "aktiv" && !canRetract(a) && a.source === "schueler"
              ? `<div class="st-absence-meta">Deine Schule hat das bearbeitet. Wende dich an sie, wenn etwas nicht stimmt.</div>`
              : ""}
          </div>
          <div class="st-absence-side">
            ${kindBadge(a)}
            ${canRetract(a)
              ? `<button class="btn btn-sm btn-secondary" data-retract="${a.id}">Zurücknehmen</button>`
              : ""}
          </div>
        </div>`).join("")}
    </div>`;
}

function renderSummary(): string {
  if (!summary) return "";
  // Ohne gehaltene Stunden waere der Bezug eine Division durch null und die Aussage leer.
  const share = summary.heldHours > 0 ? ` von ${summary.heldHours} gehaltenen Stunden` : "";
  return `
    <div class="card st-absence-summary">
      <div class="st-absence-hours">${esc(String(summary.absentHours))} Stunden versäumt</div>
      <div class="st-absence-meta">seit Kursbeginn${esc(share)}. Ausgefallener Unterricht zählt nicht mit.</div>
    </div>`;
}

async function reload() {
  [absences, summary] = await Promise.all([
    api.schuelerApp.absences.list(),
    api.schuelerApp.fehlzeiten().catch(() => null),
  ]);
}

function paint() {
  const host = currentEl?.querySelector("#ab-content");
  if (!host) return;
  host.innerHTML = `${renderSummary()}${renderList()}`;

  host.querySelectorAll<HTMLButtonElement>("[data-retract]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const ok = await confirmDialog({
        title: "Abmeldung zurücknehmen",
        message: "Die Meldung gilt dann nicht mehr. Deine Schule sieht, dass du sie zurückgenommen hast.",
        confirmLabel: "Zurücknehmen",
      });
      if (!ok) return;
      try {
        await api.schuelerApp.absences.retract(+btn.dataset.retract!);
        await reload();
        paint();
        showToast("Abmeldung zurückgenommen");
      } catch (err: any) { showToast("Fehler: " + err.message); }
    });
  });
}

export async function render(el: HTMLElement) {
  currentEl = el;
  const today = isoDate(new Date());

  el.innerHTML = `
    <div class="page-header">
      <h1>Meine Abwesenheiten</h1>
      <p style="color:#6b7280;margin:0">Melde dich ab und behalte deine Fehlzeiten im Blick.</p>
    </div>

    <div class="card card-body st-absence-form">
      <h2>Neue Abmeldung</h2>
      <div class="form-grid">
        <div class="form-group">
          <label>Von *</label>
          <input type="date" id="ab-from" value="${esc(today)}" />
        </div>
        <div class="form-group">
          <label>Bis</label>
          <input type="date" id="ab-to" />
          <small class="text-muted">Leer lassen, wenn es nur um diesen einen Tag geht.</small>
        </div>
      </div>

      <div class="form-group st-absence-toggle">
        <label class="checkbox-label">
          <input type="checkbox" id="ab-partial" /> Ich fehle nur einen Teil des Tages
        </label>
      </div>
      <div class="form-grid hidden" id="ab-times">
        <div class="form-group"><label>Von (Uhrzeit)</label><input type="time" id="ab-start" /></div>
        <div class="form-group"><label>Bis (Uhrzeit)</label><input type="time" id="ab-end" /></div>
      </div>

      <div class="form-group st-absence-toggle">
        <label class="checkbox-label">
          <input type="checkbox" id="ab-attest" /> Mir liegt ein Attest vor
        </label>
      </div>

      <div class="alert alert-error hidden" id="ab-error"></div>
      <div class="form-actions">
        <button class="btn btn-primary" id="ab-save">Abmeldung senden</button>
      </div>
    </div>

    <div id="ab-content"><div class="spinner"></div></div>`;

  const $ = <T extends HTMLElement>(sel: string) => el.querySelector<T>(sel)!;
  const errorBox = $<HTMLElement>("#ab-error");
  const showError = (message: string) => {
    errorBox.textContent = message;
    errorBox.classList.remove("hidden");
  };

  // Die Uhrzeiten stehen erst da, wenn sie gebraucht werden. Wer sich ganztägig abmeldet,
  // und das ist der Normalfall, sieht zwei Felder statt vier.
  const partial = $<HTMLInputElement>("#ab-partial");
  const times = $<HTMLElement>("#ab-times");
  partial.addEventListener("change", () => {
    times.classList.toggle("hidden", !partial.checked);
    if (!partial.checked) {
      $<HTMLInputElement>("#ab-start").value = "";
      $<HTMLInputElement>("#ab-end").value = "";
    }
  });

  $<HTMLButtonElement>("#ab-save").addEventListener("click", async () => {
    const startDate = $<HTMLInputElement>("#ab-from").value;
    if (!startDate) { showError("Bitte gib an, ab wann du fehlst"); return; }

    const startTime = partial.checked ? ($<HTMLInputElement>("#ab-start").value || null) : null;
    const endTime = partial.checked ? ($<HTMLInputElement>("#ab-end").value || null) : null;
    if (partial.checked && (!startTime || !endTime)) {
      showError("Bitte gib an, von wann bis wann du fehlst");
      return;
    }
    if (startTime && endTime && endTime <= startTime) {
      showError("Das Ende muss nach dem Beginn liegen");
      return;
    }
    const endDate = $<HTMLInputElement>("#ab-to").value || startDate;
    if (endDate < startDate) { showError("Das Ende liegt vor dem Beginn"); return; }

    const btn = $<HTMLButtonElement>("#ab-save");
    btn.disabled = true; btn.textContent = "Wird gesendet…";
    try {
      await api.schuelerApp.absences.create({
        startDate, endDate, startTime, endTime,
        attestVorgelegt: $<HTMLInputElement>("#ab-attest").checked,
      });
      errorBox.classList.add("hidden");
      // Zurück auf den Ausgangszustand, sonst meldet der nächste Klick versehentlich
      // denselben Zeitraum noch einmal.
      $<HTMLInputElement>("#ab-to").value = "";
      $<HTMLInputElement>("#ab-attest").checked = false;
      partial.checked = false;
      times.classList.add("hidden");
      $<HTMLInputElement>("#ab-start").value = "";
      $<HTMLInputElement>("#ab-end").value = "";
      await reload();
      paint();
      showToast("Abmeldung gesendet");
    } catch (err: any) {
      showError(err.message);
    } finally {
      btn.disabled = false; btn.textContent = "Abmeldung senden";
    }
  });

  try {
    await reload();
  } catch (err: any) {
    el.querySelector("#ab-content")!.innerHTML =
      `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }
  paint();
}
